"use client";

import { Link } from "@/i18n/navigation";
import type { ComponentProps } from "react";
import React, {forwardRef, useEffect, useState} from "react";

import {useAppContext} from "@/contexts/app-context";
import {getRedirectClick} from "@/lib/click";

type LinkProps = ComponentProps<typeof Link>;
type DonistaClickLinkProps = LinkProps;

export const DonistaClickLink = forwardRef<HTMLAnchorElement, DonistaClickLinkProps>(
    ({href, ...props}, ref) => {
        const {user, clientId, clientVersion} = useAppContext();

        const userId = user?.id ?? "";

        // NOTE: useState + useEffect is intentional here — do NOT replace with useMemo.
        // getRedirectClick reads browser-only APIs (navigator.language, document.referrer,
        // window.location.href, etc.) that are unavailable during SSR. Initialising state
        // with the raw href ensures server and client render the same value during hydration;
        // the effect then upgrades to the full tracking URL after mount.
        // This set-state-in-effect is accepted in .claude/react-doctor/baseline-webapp.txt.
        const [enhancedHref, setEnhancedHref] = useState<LinkProps["href"]>(href);

        useEffect(() => {
            if (typeof href !== "string") {
                setEnhancedHref(href);
                return;
            }
            setEnhancedHref(getRedirectClick(href, userId, clientId, clientVersion));
        }, [href, userId, clientId, clientVersion]);

        return (
            <Link
                {...props}
                href={enhancedHref}
                target="_blank"
                prefetch={false}
                ref={ref}
            />
        );
    }
);

DonistaClickLink.displayName = "DonistaClickLink";
